import { useEffect, useState } from "react";
import "./allsessions.css";
import { useSession } from "../../../APIContext/SessionContext";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const AllSessions = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const self_pay = user?.is_self_pay !== undefined ? user?.is_self_pay : true;
  const navigate = useNavigate();
  const { fetchMySessions, loading, checkBookingCancelStatus, cancelBooking } =
    useSession();
  const [sessionsCompleted, setSessionsCompleted] = useState([]);
  const [sessionsUpcoming, setSessionsUpcoming] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);

  function getLocalTime(utcDateTime) {
    const localDate = new Date(utcDateTime);
    return localDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  const handleCancelSession = async (sessionId) => {
    try {
      const result = await checkBookingCancelStatus(sessionId);

      if (!result) return;

      const mainMessage = result.is_late_cancel
        ? `Late cancellation — a fee will be charged.`
        : `Are you sure you want to cancel this session?`;

      const swalResult = await Swal.fire({
        title: `Cancel Session`,
        html: `
        <p>${mainMessage}</p>
        <p><strong>Cancellation Fee:</strong> $${result.cancellation_fee}</p>
      `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Proceed To Cancel",
        cancelButtonText: "Keep Session",
        reverseButtons: true,
        customClass: {
          confirmButton: "swal-confirm-btn",
          cancelButton: "swal-cancel-btn",
        },
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });

      if (swalResult.isConfirmed) {
        await cancelBooking(
          sessionId,
          result.is_late_cancel,
          result.cancellation_fee,
        );
        const updatedData = await fetchMySessions();
        if (updatedData) {
          setSessionsUpcoming(updatedData?.upcoming || []);
          setSessionsCompleted(updatedData?.completed || []);
        }
      } else {
        console.log("User canceled the cancellation popup");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong while checking cancel status.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
    }
  };

  function getLocalDate(utcDateTime) {
    const localDate = new Date(utcDateTime);
    return localDate.toLocaleDateString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  function getLocalDay(utcDateTime) {
    const localDate = new Date(utcDateTime);
    return localDate.toLocaleDateString([], {
      weekday: "long",
    });
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const [year, month, day] = dateString.split("-");
    return `${month}-${day}-${year}`;
  };

  const formatTime = (start, end) => {
    if (!start || !end) return "N/A";

    const formatToAMPM = (time) => {
      const [hours, minutes] = time.split(":").map(Number);
      const ampm = hours >= 12 ? "PM" : "AM";
      const hours12 = hours % 12 === 0 ? 12 : hours % 12;
      return `${hours12}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    };

    return `${formatToAMPM(start)} - ${formatToAMPM(end)}`;
  };

  useEffect(() => {
    const getSessions = async () => {
      const data = await fetchMySessions();
      if (data) {
        setSessionsUpcoming(data?.upcoming || []);
        setSessionsCompleted(data?.completed || []);
      }
    };
    getSessions();
  }, []);

  const toggleMenu = (index) => {
    setActiveMenu(activeMenu === index ? null : index);
  };

  return (
    <div className="sessions-container">
      {/* Upcoming Sessions */}
      <div className="sessions-header">
        <h2 style={{ width: "40%" }} className="black-text-clip">
          Your Upcoming Sessions
        </h2>
        <button
          onClick={() =>
            navigate(self_pay ? `/session-sports?type=1-1` : `/list-of-sports`)
          }
          className="new-session-btn"
        >
          <span className="black-text-clip">Schedule a New Session</span>
        </button>
      </div>

      <div className="sessions-table">
        <div className="table-header">
          <span className="black-text-clip">Package Name</span>
          <span className="black-text-clip">Player Name</span>
          <span className="black-text-clip">Type</span>
          <span className="black-text-clip">Date</span>
          <span className="black-text-clip">Time</span>
          <span className="black-text-clip">Action</span>
        </div>

        {sessionsUpcoming?.length > 0 ? (
          sessionsUpcoming?.map((session, index) => {
            const isBooked = session.status === "booked";
            const isLateCancelled = session.status === "late_cancelled";
            const isCancelled = session.status === "cancelled";

            return (
              <div
                className={`table-row ${!isBooked ? "row-disabled" : ""}`}
                key={index}
              >
                {/* Desktop View */}
                <div className="desktop-row">
                  <span className="bold black-text-clip">
                    {session?.package?.name || "N/A"}
                  </span>
                  <span className="bold location black-text-clip">
                    {session?.player?.name || "N/A"}
                  </span>
                  <span className="bold black-text-clip">
                    {session.sub_event?.is_one_on_one === true
                      ? "1 on 1"
                      : "Group" || "N/A"}
                  </span>
                  <span className="bold black-text-clip">
                    {getLocalDate(session.sub_event?.start_datetime_utc) ||
                      "N/A"}
                  </span>
                  <span className="bold black-text-clip">
                    {`${getLocalTime(session.sub_event?.start_datetime_utc)} - ${getLocalTime(session.sub_event?.end_datetime_utc)}` ||
                      "N/A"}
                  </span>

                  {isBooked ? (
                    <button
                      className="cancel-btn"
                      onClick={() => handleCancelSession(session?.id)}
                    >
                      <span className="black-text-clip">
                        Cancel The Session
                      </span>
                    </button>
                  ) : (
                    <span className="session-status">
                      {isLateCancelled
                        ? "Session Late Cancelled"
                        : "Session Cancelled"}
                    </span>
                  )}
                </div>

                {/* Mobile View */}
                <div className="mobile-row">
                  <div className="mobile-top">
                    <p className="bold black-text-clip">
                      {session?.package?.name || "N/A"}
                      <span className="session-dot1">•</span>
                      {session?.player?.name || "N/A"}
                    </p>
                    <div className="menu-wrapper">
                      <BsThreeDotsVertical
                        className="dots-icon"
                        color="#a18322"
                        size={24}
                        onClick={() => toggleMenu(index)}
                      />
                      {activeMenu === index && (
                        <div className="menu-dropdown">
                          {isBooked ? (
                            <button
                              className="cancel-option black-text-clip"
                              onClick={() => handleCancelSession(session?.id)}
                            >
                              Cancel The Session
                            </button>
                          ) : (
                            <span className="cancel-option disabled black-text-clip">
                              {isLateCancelled
                                ? "Session Late Cancelled"
                                : "Session Cancelled"}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="mobile-bottom black-text-clip">
                    <span>
                      {getLocalDate(session.sub_event?.start_datetime_utc) ||
                        "N/A"}
                    </span>
                    <span className="session-dot1">•</span>
                    {`${getLocalTime(session.sub_event?.start_datetime_utc)} - ${getLocalTime(session.sub_event?.end_datetime_utc)}` ||
                      "N/A"}
                  </p>
                </div>
              </div>
            );
          })
        ) : (
          <p
            className="black-text-clip"
            style={{ textAlign: "center", marginTop: "20px" }}
          >
            No Upcoming Sessions
          </p>
        )}
      </div>
      {/* Completed Sessions */}
      <div className="sessions-header" style={{ marginTop: "40px" }}>
        <h2 className="black-text-clip">Your Completed Sessions</h2>
      </div>

      <div className="sessions-table">
        <div className="table-header">
          <span className="black-text-clip">Package Name</span>
          <span className="black-text-clip">Player Name</span>
          <span className="black-text-clip">Type</span>
          <span className="black-text-clip">Date</span>
          <span className="black-text-clip">Time</span>
          <span className="black-text-clip">Action</span>
        </div>

        {sessionsCompleted?.length > 0 ? (
          sessionsCompleted?.map((session, index) => {
            // Determine the button text based on status
            let buttonText = "Session Completed";
            if (session.status === "cancelled")
              buttonText = "Session Cancelled";
            if (session.status === "late_cancelled")
              buttonText = "Session Late Cancelled";

            return (
              <div className="table-row" key={index}>
                {/* Desktop View */}
                <div className="desktop-row">
                  <span className="bold black-text-clip">
                    {session?.package?.name || "N/A"}
                  </span>
                  <span className="bold location black-text-clip">
                    {session?.player?.name || "N/A"}
                  </span>
                  <span className="bold black-text-clip">
                    {session.sub_event?.is_one_on_one === true
                      ? "1 on 1"
                      : "Group" || "N/A"}
                  </span>
                  <span className="bold black-text-clip">
                    {getLocalDate(session.sub_event?.start_datetime_utc) ||
                      "N/A"}
                  </span>
                  <span className="bold black-text-clip">
                    {`${getLocalTime(session.sub_event?.start_datetime_utc)} - ${getLocalTime(session.sub_event?.end_datetime_utc)}` ||
                      "N/A"}
                  </span>
                  <button className="cancel-btn session-com">Completed</button>
                </div>

                {/* Mobile View */}
                <div className="mobile-row">
                  <div className="mobile-top">
                    <p className="bold black-text-clip">
                      {session?.package?.name || "N/A"}
                      <span className="session-dot1">•</span>
                      {session?.player?.name || "N/A"}
                    </p>
                    <div className="menu-wrapper">
                      <BsThreeDotsVertical
                        className="dots-icon"
                        color="#b79527"
                        onClick={() => toggleMenu(index)}
                      />
                      {activeMenu === index && (
                        <div className="menu-dropdown">
                          <button className="cancel-option session-com">
                            Completed
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="mobile-bottom">
                    {getLocalDate(session.sub_event?.start_datetime_utc) ||
                      "N/A"}
                    <span className="session-dot1">•</span>
                    {`${getLocalTime(session.sub_event?.start_datetime_utc)} - ${getLocalTime(session.sub_event?.end_datetime_utc)}` ||
                      "N/A"}
                  </p>
                </div>
              </div>
            );
          })
        ) : (
          <p style={{ textAlign: "center", marginTop: "20px" }}>
            No Completed Sessions
          </p>
        )}
      </div>
    </div>
  );
};

export default AllSessions;
